import {
  EApiHttpMethod,
  ECardinality,
  ECardinalityConversion,
  IApiManagement,
  IApiMethods,
  IAuthorizationDto,
  IMethodInput
} from "@kuika/kuika-cl-model";
import { EApiInputStrategy } from "@kuika/kuika-cl-model/src/enums/api-input-strategy";
import { Guid } from "guid-typescript";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import { KuikaBadgeColor } from "../../ui-kit/kuika-badge/kuika-badge";

/**
 * What the API Builder's two tables show, as pure functions: the API list, one API's
 * methods, and the two derived cells (HTTP verb, authorization) the old five-column grid
 * never displayed even though the model has carried both all along.
 *
 * Kept out of the components so the derivations are testable and so the table and the
 * right panel cannot drift apart on what an untouched method actually is. The NAME rules
 * live in `api-name-modal.tsx` and `api-method-modal.tsx` — one place each. The method-input
 * model at the bottom (the List of Objects wrapper) is shared the same way by the table, the
 * input panels and the provider.
 */

/**
 * The right panel shows an unset authorization as "all roles" (`AuthorizationPanel`'s own
 * default model), so the table has to read it the same way — anything else would report a
 * method as locked down while its editor says every role can call it.
 */
const DEFAULT_AUTHORIZATION: IAuthorizationDto = {
  permittedRoles: [],
  hide: true,
  disable: false,
  allowAnonymousAccess: false,
  allowAllRoles: true
};

/**
 * Search matching, reproduced from the API left panel verbatim — transliteration quirk
 * included: the QUERY is transliterated, the candidate name is not. Changing it would
 * silently change which APIs a query surfaces.
 */
export const matchesApiSearch = (name: string, searchText: string): boolean => {
  const needle: string = KMainFunctions.turkishCharsToEnglish(searchText ?? "").toLowerCase();
  if (needle.length === 0) return true;
  return (name ?? "").toLowerCase().indexOf(needle) !== -1;
};

const byName = (a: string, b: string): number =>
  (a ?? "").localeCompare(b ?? "", undefined, { numeric: true, sensitivity: "base" });

export const filterApis = (apis: IApiManagement[], searchText: string): IApiManagement[] =>
  apis
    .filter((api: IApiManagement): boolean => matchesApiSearch(api.name, searchText))
    .sort((a: IApiManagement, b: IApiManagement): number => byName(a.name, b.name));

/** A method list is `api.json.methods`; a never-saved API has no `json` at all. */
export const getApiMethods = (api?: IApiManagement): IApiMethods[] => api?.json?.methods ?? [];

export const filterMethods = (methods: IApiMethods[], searchText: string): IApiMethods[] =>
  methods
    .filter((method: IApiMethods): boolean => matchesApiSearch(method.name, searchText))
    .sort((a: IApiMethods, b: IApiMethods): number => byName(a.name, b.name));

//  ────────────────────────────────────────────────────────────────────────────
//  Derived cells
//  ────────────────────────────────────────────────────────────────────────────

export interface IApiMethodVerb {
  label: string;
  /** Sort key. Sorting on the LABEL would order DELETE before GET, which reads as random. */
  ordinal: number;
  color: KuikaBadgeColor;
}

const VERBS: Record<EApiHttpMethod, IApiMethodVerb> = {
  [EApiHttpMethod.Get]: { label: "GET", ordinal: 0, color: "green" },
  [EApiHttpMethod.Post]: { label: "POST", ordinal: 1, color: "blue" },
  [EApiHttpMethod.Put]: { label: "PUT", ordinal: 2, color: "orange" },
  [EApiHttpMethod.Patch]: { label: "PATCH", ordinal: 3, color: "purple" },
  [EApiHttpMethod.Delete]: { label: "DELETE", ordinal: 4, color: "red" }
};

/**
 * An unset `httpMethod` is POST — the same `?? EApiHttpMethod.Post` the properties panel
 * applies to its select. The table and the editor must never disagree about what an
 * untouched method actually is.
 */
export const methodVerb = (method: IApiMethods): IApiMethodVerb =>
  VERBS[method.httpMethod ?? EApiHttpMethod.Post] ?? VERBS[EApiHttpMethod.Post];

export interface IApiMethodAuth {
  label: string;
  color: KuikaBadgeColor;
  hint?: string;
  /** Sort key: the most open access first, so a security review starts at the top. */
  ordinal: number;
}

export const methodAuthorizationOf = (method: IApiMethods): IAuthorizationDto =>
  method.authorization ?? DEFAULT_AUTHORIZATION;

export const isAnonymousMethod = (method: IApiMethods): boolean =>
  methodAuthorizationOf(method).allowAnonymousAccess === true;

export const methodAuth = (method: IApiMethods): IApiMethodAuth => {
  const authorization: IAuthorizationDto = methodAuthorizationOf(method);

  if (authorization.allowAnonymousAccess) {
    return {
      label: "Anonymous",
      color: "orange",
      hint: "Anyone can call this method without signing in.",
      ordinal: 0
    };
  }
  if (authorization.allowAllRoles) {
    return { label: "All roles", color: "blue", ordinal: 1 };
  }
  const roleCount: number = authorization.permittedRoles?.length ?? 0;
  if (roleCount > 0) {
    return { label: roleCount === 1 ? "1 role" : `${roleCount} roles`, color: "green", ordinal: 2 };
  }
  return {
    label: "No roles",
    color: "grey",
    hint: "No role can call this method yet. Add one from the Authorization panel.",
    ordinal: 3
  };
};

/** The route segment the backend actually emits for a name — `RemoveSpaces()` in
 *  `ApiGenerator.Internal_GenerateFiles`/`GenerateApi` (`Backend/Kuika.Designtime.CodeGenerator/
 *  FileGenerators/Backend/ApiGenerator.cs`), mirrored here so this reads the same path a caller
 *  would actually hit rather than the raw, space-containing name. */
const routeSegment = (name: string): string => (name ?? "").replace(/ /g, "");

/**
 * The first method's callable path — `v1/api/{ApiName}/{MethodName}`, the class-level
 * `[Route("v1/api/{Name}")]` plus the method-level `[HttpMethod, Route("{Name}")]` the backend
 * emits for every custom API (see `routeSegment`'s doc). Derived, not stored: `IApiMethods`
 * carries no `path` field of its own, so this is the only way to show it without inventing data
 * the page does not have. Undefined for an API with no methods yet.
 */
export const firstMethodPath = (api: IApiManagement): string | undefined => {
  const first: IApiMethods | undefined = getApiMethods(api)[0];
  if (!first) return undefined;
  return `/v1/api/${routeSegment(api.name)}/${routeSegment(first.name)}`;
};

export interface IApiMethodBadge {
  key: string;
  label: string;
  color: KuikaBadgeColor;
}

/** Up to `max` verb badges, in the order the methods are defined in — the list's Methods
 *  column. A caller wanting the remainder count subtracts this array's length from
 *  `getApiMethods(api).length`. */
export const methodBadges = (api: IApiManagement, max: number = 3): IApiMethodBadge[] =>
  getApiMethods(api)
    .slice(0, max)
    .map((method: IApiMethods): IApiMethodBadge => {
      const verb: IApiMethodVerb = methodVerb(method);
      return { key: method.id, label: verb.label, color: verb.color };
    });

export interface IApiAccessSummary {
  methodCount: number;
  /** Methods any caller can reach without signing in. */
  anonymousCount: number;
}

/**
 * The landing's Access column. It is DERIVED — an API carries no authorization of its own,
 * only its methods do — and it exists because "this API exposes N unauthenticated
 * endpoints" is the one thing worth seeing without opening the API.
 */
export const apiAccessSummary = (api: IApiManagement): IApiAccessSummary => {
  const methods: IApiMethods[] = getApiMethods(api);
  return {
    methodCount: methods.length,
    anonymousCount: methods.filter(isAnonymousMethod).length
  };
};

//  ────────────────────────────────────────────────────────────────────────────
//  Method inputs and the List of Objects wrapper
//  ────────────────────────────────────────────────────────────────────────────

/**
 * The inputs a caller of the method fills in. Under List of Objects they are the fields of the
 * method's single top-level input — the list wrapper the panels hide. Everything that RESOLVES a
 * binding (the symbol picker, the KV1134 validator, the generated `request.<name>`) sees the
 * wrapper itself, so only listings and counts may read through it. A List of Objects method
 * that has no wrapper yet lists what it holds at the top level — the inputs the "+" used to
 * leave there, which `addMethodInput` gathers into the wrapper on the next add.
 */
export const effectiveMethodInputs = (method: IApiMethods): IMethodInput[] => {
  const wrapper: IMethodInput | undefined = listWrapperOf(method);
  if (wrapper) return wrapper.parameters ?? [];
  return method.inputMethods ?? [];
};

export const methodInputCount = (method: IApiMethods): number => effectiveMethodInputs(method).length;

/** The one top-level input of a List of Objects method — when the method actually carries it. */
export const listWrapperOf = (method: IApiMethods): IMethodInput | undefined => {
  if (method.inputStrategy !== EApiInputStrategy.ListOfObjects) return undefined;
  const inputs: IMethodInput[] = method.inputMethods ?? [];
  const first: IMethodInput | undefined = inputs[0];
  if (inputs.length === 1 && first.cardinality === ECardinality.Many && first.parameters) return first;
  return undefined;
};

/**
 * The wrapper List of Objects keeps a method's inputs in: one top-level input, `Items`, a list of
 * `{Method}_Item` objects whose fields are the inputs. Built here only, so the Payload As switch
 * and the input panel's "+" produce the same shape.
 */
export const createListWrapper = (method: IApiMethods, fields: IMethodInput[]): IMethodInput =>
  ({
    id: Guid.create().toString(),
    name: "Items",
    type: `${method.name}_Item`,
    isRequired: true,
    isObject: true,
    cardinality: ECardinality.Many,
    parameters: fields
  }) as IMethodInput;

/**
 * Adds an input where the method keeps them: at the top level for Object, inside the wrapper for
 * List of Objects. A List of Objects method with no wrapper yet — List chosen before any input
 * existed — gets the wrapper now, around this input and whatever the top level already held.
 */
export const addMethodInput = (method: IApiMethods, input: IMethodInput): void => {
  const wrapper: IMethodInput | undefined = listWrapperOf(method);
  if (wrapper) {
    if (!wrapper.parameters) wrapper.parameters = [];
    wrapper.parameters.push(input);
    return;
  }
  const inputs: IMethodInput[] = [...(method.inputMethods ?? []), input];
  if (method.inputStrategy === EApiInputStrategy.ListOfObjects) {
    method.inputMethods = [createListWrapper(method, inputs)];
  } else {
    method.inputMethods = inputs;
  }
};

/** Swaps an edited input in by id — for List of Objects, inside the wrapper. */
export const replaceMethodInput = (method: IApiMethods, input: IMethodInput): boolean => {
  const inputs: IMethodInput[] = effectiveMethodInputs(method);
  const index: number = inputs.findIndex((candidate: IMethodInput): boolean => candidate.id === input.id);
  if (index < 0) return false;
  inputs[index] = input;
  return true;
};

/**
 * A binding's field path through a list element, as the symbol picker writes it
 * (`addCardinalityConverterToSymbol` in `designer-symbol-picker-engine.tsx`): `<field>`,
 * `<field>[first|last]`, then `.<deeper>`. The Payload As switch and the field rename in the
 * input panels read it back to rewrite a binding.
 */
export interface IElementFieldPath {
  field: string;
  converter?: ECardinalityConversion;
  rest?: string;
}

const ELEMENT_FIELD_PATH: RegExp = /^([^.[\]]+)(?:\[(first|last)\])?(?:\.(.+))?$/;

export const parseElementFieldPath = (fieldName: unknown): IElementFieldPath | undefined => {
  if (typeof fieldName !== "string") return undefined;
  const match: RegExpMatchArray | null = fieldName.match(ELEMENT_FIELD_PATH);
  if (!match) return undefined;
  let converter: ECardinalityConversion | undefined;
  if (match[2] === "first") {
    converter = ECardinalityConversion.first;
  } else if (match[2] === "last") {
    converter = ECardinalityConversion.last;
  }
  return { field: match[1], converter, rest: match[3] };
};
